import type { DecisionSnapshot, OrderIntent } from '../types/snapshot'

/**
 * Mirror of Guardian.Core/Facts.cs. Add a fact in one and you add it in the other, in
 * the same commit — the Tier 1 / Tier 2 mismatch alarm will catch a divergence at
 * runtime, but review is cheaper than an alert.
 *
 * Booleans are carried as 0 or 1 so one map serves both predicate kinds.
 */
export const F = {
  riskPercent: 'order.riskPercent',
  notional: 'order.notional',
  volumeUnits: 'order.volumeUnits',
  estimatedMargin: 'order.estimatedMargin',
  hasStopLoss: 'order.hasStopLoss',
  stopDistancePercent: 'order.stopDistancePercent',
  symbolAllowed: 'order.symbolAllowed',

  equity: 'account.equity',
  marginLevel: 'account.marginLevel',
  openPositions: 'account.openPositions',
  tradesToday: 'account.tradesToday',

  dailyBudgetRemaining: 'budget.dailyRemaining',
  weeklyBudgetRemaining: 'budget.weeklyRemaining',

  symbolExposureAfter: 'exposure.symbolAfter',
  symbolExposurePercentAfter: 'exposure.symbolPercentAfter',

  isLocked: 'state.isLocked',
  inCooldown: 'state.inCooldown',
  inBlackout: 'state.inBlackout',
  inTradingWindow: 'state.inTradingWindow',

  minutesSinceLossOnSymbol: 'behaviour.minutesSinceLossOnSymbol',
  volumeRatioVsLastEntry: 'behaviour.volumeRatioVsLastEntry',

  approvalRequired: 'approval.required',
} as const

/** Stands in for "never happened", matching Facts.Never in C#. */
export const NEVER = 1_000_000

export interface FactSet {
  values: Readonly<Record<string, number>>
  riskPercent: number | null
  estimatedMargin: number
  notional: number
}

const ms = (iso: string) => Date.parse(iso)

/**
 * @param nowMs corrected server time in epoch milliseconds — NOT Date.now(). A fast
 *   client clock would otherwise end a cooldown early. See serverClock.ts.
 */
export function resolveFacts(
  snapshot: DecisionSnapshot,
  intent: OrderIntent,
  nowMs: number,
): FactSet {
  const limits = snapshot.symbols[String(intent.symbol)]
  const units = intent.volume / 100
  const notional = units * intent.price
  const estimatedMargin = limits ? notional * limits.marginRate : 0

  let riskPercent: number | null = null
  let stopDistancePercent = 0
  if (intent.stopLoss !== undefined && intent.price > 0 && snapshot.equity > 0) {
    const distance = Math.abs(intent.price - intent.stopLoss)
    stopDistancePercent = (distance / intent.price) * 100
    riskPercent = ((units * distance) / snapshot.equity) * 100
  }

  const exposureBefore = snapshot.exposureBySymbol[String(intent.symbol)] ?? 0
  const grossBefore = snapshot.grossExposureBySymbol[String(intent.symbol)] ?? 0
  const signed = intent.side === 'Buy' ? notional : -notional

  // On a hedged account a sell does not cancel a long — both stay open, both cost margin,
  // and both can lose. Netting would let a trader double real exposure while the
  // concentration rule reported zero.
  const exposureAfter = snapshot.isHedged
    ? grossBefore + notional
    : Math.abs(exposureBefore + signed)

  let minutesSinceLoss = NEVER
  if (snapshot.lastLoss && snapshot.lastLoss.symbol === intent.symbol) {
    minutesSinceLoss = (nowMs - ms(snapshot.lastLoss.at)) / 60_000
  }

  let volumeRatio = 0
  if (
    snapshot.lastEntry &&
    snapshot.lastEntry.symbol === intent.symbol &&
    snapshot.lastEntry.volume > 0
  ) {
    volumeRatio = intent.volume / snapshot.lastEntry.volume
  }

  const inWindow =
    snapshot.tradingWindows.length === 0 ||
    snapshot.tradingWindows.some((w) => nowMs >= ms(w.from) && nowMs < ms(w.to))

  const values: Record<string, number> = {
    [F.riskPercent]: riskPercent ?? 0,
    [F.notional]: notional,
    [F.volumeUnits]: units,
    [F.estimatedMargin]: estimatedMargin,
    [F.hasStopLoss]: intent.stopLoss !== undefined ? 1 : 0,
    [F.stopDistancePercent]: stopDistancePercent,
    [F.symbolAllowed]: limits?.allowed ? 1 : 0,

    [F.equity]: snapshot.equity,
    [F.marginLevel]: snapshot.marginLevel,
    [F.openPositions]: snapshot.openPositionCount,
    [F.tradesToday]: snapshot.tradesTakenToday,

    [F.dailyBudgetRemaining]: snapshot.dailyLossBudgetRemaining,
    [F.weeklyBudgetRemaining]: snapshot.weeklyLossBudgetRemaining,

    [F.symbolExposureAfter]: exposureAfter,
    [F.symbolExposurePercentAfter]:
      snapshot.equity > 0 ? (exposureAfter / snapshot.equity) * 100 : 0,

    [F.isLocked]: snapshot.lockedUntil && nowMs < ms(snapshot.lockedUntil) ? 1 : 0,
    [F.inCooldown]: snapshot.cooldownUntil && nowMs < ms(snapshot.cooldownUntil) ? 1 : 0,
    [F.inBlackout]: snapshot.blackoutWindows.some((w) => nowMs >= ms(w.from) && nowMs < ms(w.to))
      ? 1
      : 0,
    [F.inTradingWindow]: inWindow ? 1 : 0,

    [F.minutesSinceLossOnSymbol]: minutesSinceLoss,
    [F.volumeRatioVsLastEntry]: volumeRatio,

    [F.approvalRequired]: notional > snapshot.peerApprovalThreshold ? 1 : 0,
  }

  return { values, riskPercent, estimatedMargin, notional }
}
